
import React, { useState, useRef, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface TimeDialSelectorProps {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
}

export const TimeDialSelector = ({ value, onChange, placeholder }: TimeDialSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState(7);
  const [minutes, setMinutes] = useState(0);
  const [period, setPeriod] = useState<'AM' | 'PM'>('PM');

  // Parse initial value or set default
  useEffect(() => {
    if (value) {
      const [timeStr] = value.split(' ');
      const [h, m] = timeStr.split(':').map(Number);
      
      if (h === 0) {
        setHours(12);
        setPeriod('AM');
      } else if (h < 12) {
        setHours(h);
        setPeriod('AM');
      } else if (h === 12) {
        setHours(12);
        setPeriod('PM');
      } else {
        setHours(h - 12);
        setPeriod('PM');
      }
      
      setMinutes(m);
    } else {
      // Set default time to 7:00 PM
      setHours(7);
      setMinutes(0);
      setPeriod('PM');
      const defaultTime = formatTime(7, 0, 'PM');
      onChange(defaultTime);
    }
  }, [value, onChange]);

  const formatTime = (h: number, m: number, p: 'AM' | 'PM') => {
    const hour24 = p === 'AM' ? (h === 12 ? 0 : h) : (h === 12 ? 12 : h + 12);
    return `${hour24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const formatDisplayTime = (h: number, m: number, p: 'AM' | 'PM') => {
    return `${h}:${m.toString().padStart(2, '0')} ${p}`;
  };

  const updateTime = () => {
    const time24 = formatTime(hours, minutes, period);
    onChange(time24);
    setIsOpen(false);
  };

  // Generate arrays for time options
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const minuteOptions = [0, 15, 30, 45];
  const periodOptions = ['AM', 'PM'];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-12 px-4 bg-gray-200 border-0 text-black rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-left transition-all duration-200 hover:bg-gray-250"
      >
        {formatDisplayTime(hours, minutes, period)}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-6">
          <div className="text-center mb-4">
            <p className="text-sm text-gray-600">Select your preferred time</p>
          </div>
          
          <div className="flex items-center justify-center space-x-6">
            {/* Hours Selector */}
            <div className="flex flex-col items-center flex-1">
              <div className="text-xs text-gray-600 mb-2 font-medium">Hour</div>
              <ScrollArea className="h-32 w-full border rounded-md">
                <div className="p-2 space-y-1">
                  {hourOptions.map((hour) => (
                    <button
                      key={hour}
                      onClick={() => setHours(hour)}
                      className={cn(
                        "w-full text-center p-2 rounded font-medium transition-colors text-sm",
                        hours === hour
                          ? "bg-green-50 text-green-600 border border-green-200"
                          : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            <div className="text-2xl font-bold text-black self-center">:</div>

            {/* Minutes Selector */}
            <div className="flex flex-col items-center flex-1">
              <div className="text-xs text-gray-600 mb-2 font-medium">Minute</div>
              <ScrollArea className="h-32 w-full border rounded-md">
                <div className="p-2 space-y-1">
                  {minuteOptions.map((minute) => (
                    <button
                      key={minute}
                      onClick={() => setMinutes(minute)}
                      className={cn(
                        "w-full text-center p-2 rounded font-medium transition-colors text-sm",
                        minutes === minute
                          ? "bg-green-50 text-green-600 border border-green-200"
                          : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {minute.toString().padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* AM/PM Selector */}
            <div className="flex flex-col items-center flex-1">
              <div className="text-xs text-gray-600 mb-2 font-medium">Period</div>
              <ScrollArea className="h-32 w-full border rounded-md">
                <div className="p-2 space-y-1">
                  {periodOptions.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p as 'AM' | 'PM')}
                      className={cn(
                        "w-full text-center p-2 rounded font-medium transition-colors text-sm",
                        period === p
                          ? "bg-green-50 text-green-600 border border-green-200"
                          : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          <div className="flex space-x-2 mt-6">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-black rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={updateTime}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-400 to-green-500 text-white rounded-lg hover:from-cyan-500 hover:to-green-600 transition-all duration-200"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
